import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ArrowRight, Eye, Mic } from "lucide-react";
import { useTranslation } from "react-i18next";

import { GroupAvatar } from "@/components/study-group/GroupAvatar";
import { useGroupTopicsOfDay } from "@/hooks/use-group-topic-of-day";
import { slateCardClass, slateSurfaceClass } from "@/lib/card-styles";
import { typeMetaClass, typeTitleClass } from "@/lib/typography";
import { cn, slugify } from "@/lib/utils";

interface GroupTopicOfTheDayProps {
  enabled?: boolean;
  onOpenTopic: (slug: string, title: string) => void;
  /** Opens the session read-only, mirroring TopicOfTheDay's "View". */
  onViewTopic?: (slug: string, title: string) => void;
  onOpenGroup: (groupId: string) => void;
}

const GroupTopicOfTheDay = ({ enabled = true, onOpenTopic, onViewTopic, onOpenGroup }: GroupTopicOfTheDayProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: groups, isLoading } = useGroupTopicsOfDay({ enabled });

  const topics = useMemo(() => (groups ?? []).filter(Boolean), [groups]);

  if (!enabled || isLoading || topics.length === 0) return null;

  return (
    <section aria-labelledby="group-topic-of-the-day-heading">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2
          id="group-topic-of-the-day-heading"
          className={cn(typeTitleClass, "inline-flex items-center gap-2")}
        >
          <Users className="h-4 w-4 text-primary" aria-hidden="true" />
          From your groups
        </h2>
        <span className={typeMetaClass}>
          {topics.length} {topics.length === 1 ? "group" : "groups"}
        </span>
      </div>

      <ul className="grid gap-3 sm:grid-cols-2">
        {topics.map((group) => {
          const title = group.topic ?? t("home.topicOfDay.title", "Topic of the day");
          const slug = group.slug || slugify(title) || null;
          const openable = !!slug;

          return (
            <li key={group.id ?? `${group.groupId}-${title}`}>
              <article
                className={cn(
                  slateCardClass,
                  "group relative flex h-full flex-col gap-3 overflow-hidden p-4 pl-5",
                  "transition-all duration-200 hover:border-primary/35 hover:shadow-md"
                )}
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 w-1 bg-primary/80"
                />

                <div className="flex items-start gap-2.5">
                  <span aria-hidden="true" className="shrink-0">
                    <GroupAvatar
                      name={group.groupName}
                      avatarUrl={group.groupAvatarUrl}
                      icon={group.groupIcon}
                      size="md"
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => onOpenGroup(group.groupId)}
                      className="block max-w-full truncate text-left text-xs font-medium text-stone-500 transition-colors hover:text-primary dark:text-stone-400"
                      title={group.groupName}
                    >
                      {group.groupName}
                    </button>
                    <h3 className="mt-0.5 line-clamp-2 font-display text-base font-semibold leading-snug tracking-tight text-stone-900 sm:text-lg dark:text-stone-50">
                      {title}
                    </h3>
                  </div>
                </div>

                <div className={cn("mt-auto flex items-center justify-between gap-2", slateSurfaceClass, "!py-2")}>
                  <span
                    className="min-w-0 truncate text-xs text-stone-500 dark:text-stone-400"
                    title={group.set_at ? new Date(group.set_at).toLocaleString() : undefined}
                  >
                    {formatSetAgo(group.set_at)}
                  </span>
                  <div className="flex shrink-0 items-center gap-1">
                    {onViewTopic && (
                      <button
                        type="button"
                        disabled={!openable}
                        onClick={() => {
                          if (slug) onViewTopic(slug, title);
                        }}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                          openable
                            ? "text-stone-600 hover:bg-stone-200/70 dark:text-stone-300 dark:hover:bg-stone-700/70"
                            : "cursor-default text-stone-400 opacity-60"
                        )}
                      >
                        {t("home.topicOfDay.view", "View")}
                        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={!openable}
                      onClick={() => {
                        if (slug) onOpenTopic(slug, title);
                      }}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
                        openable
                          ? "bg-primary/10 text-primary hover:bg-primary/15"
                          : "cursor-default text-stone-400 opacity-60"
                      )}
                    >
                      <Mic className="h-3 w-3" aria-hidden="true" />
                      {t("home.topicOfDay.practise", "Practise")}
                      <ArrowRight
                        className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

function formatSetAgo(iso: string | null | undefined): string {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";

  const elapsed = Date.now() - then;
  if (elapsed < 60000) return "just now";

  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(elapsed / 3600000);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(elapsed / 86400000);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export { GroupTopicOfTheDay };
export default GroupTopicOfTheDay;
